import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Users, ShoppingBag, LogOut, Shield, Menu, X, Building2 } from 'lucide-react';
import AdminStats from './AdminStats';
import AdminUsers from './AdminUsers';
import AdminOrders from './AdminOrders';
import AdminCompanies from './AdminCompanies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchAdmin = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/me`, {
        withCredentials: true
      });
      setAdmin(response.data);
    } catch (error) {
      console.error('Admin auth failed:', error);
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAdmin();
  }, [fetchAdmin]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
      navigate('/admin');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path) => {
    if (path === '/admin/dashboard') return location.pathname === '/admin/dashboard';
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA]">
        <div className="w-16 h-16 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#0A0A0A]/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#B8935A]" />
            <span className="text-lg font-bold text-[#0A0A0A]">Admin Panel</span>
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-[#0A0A0A] p-2">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div className="flex pt-16 lg:pt-0">
        {/* Sidebar */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#0A0A0A]/10 p-6 transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} lg:min-h-screen`}>
          <div className="mb-8 hidden lg:flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#B8935A]" />
            <div>
              <h1 className="text-xl font-bold text-[#0A0A0A]">Admin Panel</h1>
              <p className="text-xs text-[#525252]">Sol Digital</p>
            </div>
          </div>

          <nav className="space-y-2">
            <Link
              to="/admin/dashboard"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/admin/dashboard') && location.pathname === '/admin/dashboard' ? 'bg-[#B8935A]/20 text-[#B8935A]' : 'text-[#525252] hover:bg-[#EBE6DC]'}`}
              data-testid="admin-nav-stats"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
            <Link
              to="/admin/dashboard/users"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/admin/dashboard/users') ? 'bg-[#B8935A]/20 text-[#B8935A]' : 'text-[#525252] hover:bg-[#EBE6DC]'}`}
              data-testid="admin-nav-users"
            >
              <Users className="w-5 h-5" />
              <span>Users</span>
            </Link>
            <Link
              to="/admin/dashboard/orders"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/admin/dashboard/orders') ? 'bg-[#B8935A]/20 text-[#B8935A]' : 'text-[#525252] hover:bg-[#EBE6DC]'}`}
              data-testid="admin-nav-orders"
            >
              <ShoppingBag className="w-5 h-5" />
              <span>Orders</span>
            </Link>
            <Link
              to="/admin/dashboard/companies"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/admin/dashboard/companies') ? 'bg-[#B8935A]/20 text-[#B8935A]' : 'text-[#525252] hover:bg-[#EBE6DC]'}`}
              data-testid="admin-nav-companies"
            >
              <Building2 className="w-5 h-5" />
              <span>Companies</span>
            </Link>
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#B8935A]/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#B8935A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0A0A0A] truncate">{admin?.name}</p>
                <p className="text-xs text-[#525252] truncate">{admin?.role}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-[#525252] hover:text-[#0A0A0A] hover:bg-[#EBE6DC]"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)}></div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 w-full lg:w-auto overflow-x-hidden">
          <Routes>
            <Route index element={<AdminStats />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="companies" element={<AdminCompanies />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
